import React, { useRef, useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

/**
 * Reusable chat input component for modal footers.
 * Works with TaskAssistantChat via ref to handle sending messages and file attachments.
 * Includes voice recording with speech-to-text transcription (matches ReplyModal pattern).
 *
 * @param {Object} props
 * @param {React.RefObject} props.chatAssistantRef - Ref to TaskAssistantChat component
 * @param {string} props.inputValue - Current input value
 * @param {Function} props.onInputChange - Callback when input changes
 * @param {string} [props.placeholder] - Input placeholder text
 * @param {Function} [props.onClearChat] - Callback to clear chat history
 */
const ChatAssistantInput = ({
  chatAssistantRef,
  inputValue,
  onInputChange,
  placeholder = "What can I take off your mind right now?",
  onClearChat,
  autoFocus = false
}) => {
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);

  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const intervalRef = useRef(null);
  const isCancelledRef = useRef(false);
  const isCopyToTextAreaRef = useRef(false); // Use ref to capture value at click time
  const inputValueRef = useRef(inputValue); // Track current input value for async callback

  // Keep inputValueRef in sync with inputValue
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  // Auto-focus input when autoFocus prop is true
  useEffect(() => {
    if (autoFocus && textInputRef.current) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        textInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && chatAssistantRef?.current) {
      chatAssistantRef.current.selectFile(file);
    }
    e.target.value = '';
  };

  const handleSend = () => {
    if (chatAssistantRef?.current) {
      chatAssistantRef.current.triggerSend();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Audio to text transcription
  const audioToText = async (audioBlob) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.mp3');

      const apiUrl = process.env.REACT_APP_ZOOMEY_API_URL || '';
      const response = await fetch(`${apiUrl}/speech-to-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Transcription failed');
      }

      const data = await response.json();
      return data.text || '';
    } catch (error) {
      console.error('[ChatAssistantInput] Audio transcription error:', error);
      return '';
    }
  };

  // Start voice recording
  const handleStartRecording = async () => {
    try {
      audioChunksRef.current = [];
      isCancelledRef.current = false;
      isCopyToTextAreaRef.current = false;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // If cancelled, don't process
        if (isCancelledRef.current) {
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
          }
          return;
        }

        // Create audio blob and transcribe
        if (audioChunksRef.current.length > 0) {
          setIsTranscribing(true);
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mp3' });
          const transcribedText = await audioToText(audioBlob);

          if (transcribedText) {
            if (isCopyToTextAreaRef.current) {
              // Add to text area (user can edit before sending)
              // Use ref to get current input value (not stale closure value)
              const currentInput = inputValueRef.current;
              const newValue = currentInput ? `${currentInput} ${transcribedText}` : transcribedText;
              onInputChange(newValue);
            } else {
              // Direct send - set text and trigger send
              onInputChange(transcribedText);
              // Small delay to ensure state is updated before sending
              setTimeout(() => {
                if (chatAssistantRef?.current) {
                  chatAssistantRef.current.triggerSend();
                }
              }, 100);
            }
          }
          setIsTranscribing(false);
        }

        // Cleanup
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      intervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('[ChatAssistantInput] Microphone access error:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  // Stop recording and send directly
  const handleStopAndSend = () => {
    if (mediaRecorderRef.current && isRecording) {
      isCopyToTextAreaRef.current = false;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  // Stop recording and add to text area (user can edit)
  const handleAddToTextArea = () => {
    if (mediaRecorderRef.current && isRecording) {
      isCopyToTextAreaRef.current = true;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  // Cancel recording without transcribing
  const handleCancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      isCancelledRef.current = true;
      audioChunksRef.current = [];
      mediaRecorderRef.current.stop();

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }

      setIsRecording(false);
      setRecordingTime(0);
      isCopyToTextAreaRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  const isLoading = chatAssistantRef?.current?.isLoading || isTranscribing;
  const hasFilePreview = chatAssistantRef?.current?.filePreview !== null;

  // Button style helper
  const buttonStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: '1px solid var(--border-color, #e2e8f0)',
    background: 'transparent',
    color: 'var(--text, #64748b)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0
  };

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    }}>
      {/* Processing voice indicator */}
      {isTranscribing && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          borderRadius: '8px',
          background: 'var(--chatbot-bg, #f8fafc)',
        }}>
          <div style={{
            width: '16px',
            height: '16px',
            border: '2px solid var(--border-color, rgba(255,255,255,0.3))',
            borderTop: '3px solid var(--teal-bg, #14b8a6)',
            borderRadius: '50%',
            animation: 'spin 0.5s linear infinite',
          }} />
          <i className="bi bi-mic-fill" style={{ fontSize: '14px', opacity: 0.7, color: 'var(--text)' }} />
          <span style={{ fontSize: '12px', opacity: 0.7, color: 'var(--text)' }}>
            Processing your voice...
          </span>
        </div>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*,.pdf,.csv,.xlsx,.xls"
        style={{ display: 'none' }}
      />

      {/* Main layout: [Refresh] [Input with teal border] [Send/Recording buttons] [Attachment] [Mic] */}
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
      }}>
        {/* Clear/Refresh chat button - Always visible on left */}
        {onClearChat && (
          <button
            onClick={onClearChat}
            disabled={isLoading || isRecording}
            title="Clear chat history"
            style={{
              ...buttonStyle,
              opacity: (isLoading || isRecording) ? 0.5 : 1,
              cursor: (isLoading || isRecording) ? 'not-allowed' : 'pointer'
            }}
          >
            <i className="bi bi-arrow-counterclockwise" style={{ fontSize: '0.85rem' }}></i>
          </button>
        )}

        {/* Input field with teal border - Always visible */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          background: 'var(--background, #fff)',
          border: '2px solid var(--teal-bg, #14b8a6)',
          borderRadius: '8px',
          padding: '8px 12px',
        }}>
          <input
            ref={textInputRef}
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading || isRecording}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              color: 'var(--text, #1e293b)',
              outline: 'none',
              opacity: isRecording ? 0.5 : 1,
            }}
          />
        </div>

        {/* Recording UI - replaces Send/Attachment/Mic with recording controls */}
        {isRecording ? (
          <>
            {/* Add to Text Area button */}
            <button
              onClick={handleAddToTextArea}
              title="Add to text area"
              style={{
                ...buttonStyle,
                borderRadius: '8px',
              }}
            >
              <i className="bi bi-journal-text" style={{ fontSize: '1rem', opacity: 0.8 }}></i>
            </button>

            {/* Cancel Recording */}
            <button
              onClick={handleCancelRecording}
              title="Cancel recording"
              style={{
                ...buttonStyle,
                borderRadius: '8px',
                background: 'var(--background, #fff)',
              }}
            >
              <i className="bi bi-x-lg" style={{ fontSize: '0.8rem', color: '#ef4444', fontWeight: 'bold' }}></i>
            </button>

            {/* Stop and Send */}
            <button
              onClick={handleStopAndSend}
              title="Stop and send"
              style={{
                ...buttonStyle,
                background: '#ef4444',
                border: 'none',
                color: 'white'
              }}
            >
              <i className="bi bi-stop-fill" style={{ fontSize: '0.9rem' }}></i>
            </button>

            {/* Recording timer - after stop button */}
            <span style={{
              fontSize: '0.85rem',
              color: 'var(--text, #666)',
              fontFamily: 'inherit',
              minWidth: '30px',
            }}>
              {recordingTime}s
            </span>
          </>
        ) : (
          <>
            {/* Send button - Outside on right */}
            <button
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              style={{
                ...buttonStyle,
                border: 'none',
                background: 'var(--teal-bg, #14b8a6)',
                color: 'white',
                opacity: (!inputValue.trim() || isLoading) ? 0.5 : 1,
                cursor: (!inputValue.trim() || isLoading) ? 'not-allowed' : 'pointer'
              }}
            >
              <i className="bi bi-send-fill" style={{ fontSize: '0.75rem', transform: 'translateX(1px)' }}></i>
            </button>

            {/* Attachment button - Outside on right */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || hasFilePreview}
              title="Attach file"
              style={{
                ...buttonStyle,
                opacity: (isLoading || hasFilePreview) ? 0.5 : 1,
                cursor: (isLoading || hasFilePreview) ? 'not-allowed' : 'pointer'
              }}
            >
              <i className="bi bi-paperclip" style={{ fontSize: '0.85rem' }}></i>
            </button>

            {/* Voice Recording button - Outside on right */}
            <button
              onClick={handleStartRecording}
              disabled={isLoading}
              title="Start recording"
              style={{
                ...buttonStyle,
                opacity: isLoading ? 0.5 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              <i className="bi bi-mic" style={{ fontSize: '0.85rem' }}></i>
            </button>
          </>
        )}
      </div>

      {/* Spin animation for processing indicator */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ChatAssistantInput;
